(function () {
  window.dataLayer = window.dataLayer || [];

  const state = window.ORAnalyticsData || {};
  const pushedKeys = new Set();

  function push(payload) {
    window.dataLayer.push(payload);
  }

  function resetEcommerce() {
    push({ ecommerce: null });
  }

  function normalizePath(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return String(url || '').split('?')[0];
    }
  }

  function itemId(item) {
    const key = state.config?.ga?.productIdentifier || 'sku';
    return item?.[key] || item?.sku || item?.variant_id || item?.product_id;
  }

  function ga4Item(item, overrides) {
    if (!item) return null;

    return {
      item_id: itemId(item),
      item_variant_id: item.variant_id,
      item_product_id: item.product_id,
      item_name: item.name || item.title,
      item_list_name: item.list,
      item_list_id: item.list,
      item_brand: item.brand || item.vendor,
      item_category: item.category,
      item_variant: item.variant_title || item.variant,
      index: item.position,
      quantity: item.quantity,
      price: Number(item.price || 0),
      ...overrides,
    };
  }

  function classicProduct(item, overrides) {
    if (!item) return null;

    return {
      sku: item.sku || item.variant_id,
      variant_id: item.variant_id,
      product_id: item.product_id,
      name: item.name || item.title,
      title: item.title || item.name,
      brand: item.brand || item.vendor,
      vendor: item.vendor || item.brand,
      category: item.category,
      variant: item.variant || item.variant_title,
      variant_title: item.variant_title || item.variant,
      quantity: item.quantity,
      price: Number(item.price || 0),
      position: item.position,
      list: item.list,
      url: item.url,
      ...overrides,
    };
  }

  function compactProduct(product) {
    return Object.fromEntries(Object.entries(product || {}).filter(([, value]) => value !== undefined && value !== null));
  }

  function ecommerceEvent(event, ecommerce, extra) {
    resetEcommerce();
    push({
      event,
      ecommerce,
      ...extra,
    });
  }

  function pushInit() {
    const config = {
      ga: {
        id: state.config?.ga?.id || undefined,
        productIdentifier: state.config?.ga?.productIdentifier || 'sku',
      },
    };

    push({
      event: 'init_global_marketing_variable',
      config,
      ecommerce: {
        currencyCode: state.config?.currencyCode,
      },
      user_properties: {
        visitor_type: state.config?.visitorType || 'guest',
      },
      marketing: {
        user_id: state.config?.userId || undefined,
      },
    });
  }

  function pushViewItem() {
    if (state.page?.type !== 'product' || !state.product) return;
    const product = compactProduct(classicProduct(state.product));

    ecommerceEvent('dl_ga4_view_item', {
      currencyCode: state.config?.currencyCode,
      detail: {
        products: [product],
      },
    });
  }

  function pushViewItemList() {
    const items = state.list?.items || [];
    if (!items.length) return;

    ecommerceEvent('dl_ga4_view_item_list', {
      currencyCode: state.config?.currencyCode,
      item_list_name: state.list?.name || state.page?.path,
      item_list_id: state.list?.id || state.page?.path,
      impressions: items.map((item) =>
        compactProduct(
          classicProduct(item, {
            list: state.list?.name || item.list || state.page?.path,
          })
        )
      ),
    });
  }

  function pushSearch() {
    if (!state.page?.searchTerm) return;

    push({
      event: 'dl_ga4_search',
      search_term: state.page.searchTerm,
    });
  }

  function pushViewCart() {
    if (state.page?.type !== 'cart') return;
    const items = state.cart?.items || [];

    ecommerceEvent(
      'dl_ga4_view_cart',
      {
        currencyCode: state.config?.currencyCode,
        impressions: items.map((item) => compactProduct(classicProduct(item))),
      },
      {
        cart_total: Number(state.cart?.total || 0),
      }
    );
  }

  function pushSelectItem(item) {
    if (!item) return;

    ecommerceEvent('dl_select_item', {
      currencyCode: state.config?.currencyCode,
      item_list_name: state.list?.name || item.list || state.page?.path,
      item_list_id: state.list?.id || item.list || state.page?.path,
      click: {
        products: [
          compactProduct(
            classicProduct(item, {
              list: state.list?.name || item.list || state.page?.path,
            })
          ),
        ],
      },
    });
  }

  function itemFromCollectionFeatureLink(link) {
    if (!link) return null;

    return compactProduct({
      sku: link.dataset.orProductSku,
      variant_id: link.dataset.orProductVariantId,
      product_id: link.dataset.orProductId,
      name: link.dataset.orProductTitle,
      title: link.dataset.orProductTitle,
      brand: link.dataset.orProductVendor,
      vendor: link.dataset.orProductVendor,
      category: link.dataset.orProductType,
      variant: link.dataset.orProductVariant,
      variant_title: link.dataset.orProductVariant,
      quantity: 1,
      price: Number(link.dataset.orProductPrice || 0),
      position: Number(link.dataset.orProductPosition || 0),
      list: link.dataset.orCollectionTitle,
      url: link.href,
    });
  }

  function pushCollectionFeatureClick(link) {
    const root = link?.closest('[data-or-collection-feature]');
    const item = itemFromCollectionFeatureLink(link);
    if (!root || !item) return;

    const source = root.dataset.orCollectionFeatureSource || state.page?.type || 'unknown';
    const collectionHandle = link.dataset.orCollectionHandle || root.dataset.orCollectionFeatureCollection;
    const collectionTitle = link.dataset.orCollectionTitle || collectionHandle;
    const listName = `OR Collection Feature - ${collectionTitle}`;

    ecommerceEvent(
      'or_collection_feature_click',
      {
        currencyCode: state.config?.currencyCode,
        item_list_name: listName,
        item_list_id: collectionHandle,
        click: {
          products: [
            compactProduct(
              classicProduct(item, {
                list: listName,
              })
            ),
          ],
        },
      },
      {
        collection_feature: {
          source,
          page_type: state.page?.type,
          page_path: state.page?.path,
          section_id: root.dataset.orCollectionFeatureSectionId,
          collection_handle: collectionHandle,
          collection_title: collectionTitle,
          product_url: item.url,
        },
      }
    );
  }

  function pushAddToCart(item, quantity) {
    if (!item) return;
    const addItem = compactProduct(classicProduct(item, { quantity: Number(quantity || item.quantity || 1) }));
    const key = `add:${addItem.variant_id}:${addItem.quantity}:${Date.now().toString().slice(0, -3)}`;
    if (pushedKeys.has(key)) return;
    pushedKeys.add(key);

    ecommerceEvent(
      'dl_ga4_add_to_cart',
      {
        currencyCode: state.config?.currencyCode,
        add: {
          products: [addItem],
        },
      },
      {
        cart_total: Number(state.cart?.total || 0),
      }
    );
  }

  function pushRemoveFromCart(item, quantity) {
    if (!item) return;
    const removeItem = compactProduct(classicProduct(item, { quantity: Number(quantity || item.quantity || 1) }));

    ecommerceEvent(
      'dl_ga4_remove_from_cart',
      {
        currencyCode: state.config?.currencyCode,
        remove: {
          products: [removeItem],
        },
      },
      {
        cart_total: Number(state.cart?.total || 0),
      }
    );
  }

  function pushBeginCheckout() {
    const items = state.cart?.items || [];
    if (!items.length) return;

    ecommerceEvent(
      'dl_begin_checkout',
      {
        currencyCode: state.config?.currencyCode,
        checkout: {
          products: items.map((item) => compactProduct(classicProduct(item))),
        },
      },
      {
        cart_total: Number(state.cart?.total || 0),
      }
    );
  }

  function pushWhatsAppClick(link) {
    if (!link) return;

    push({
      event: 'or_whatsapp_click',
      link_url: link.href,
      link_text: (link.innerText || link.getAttribute('aria-label') || 'WhatsApp').trim(),
      whatsapp_context: link.closest('.or-wa-float') ? 'floating_button' : state.page?.type || 'site',
      page_type: state.page?.type,
      page_path: state.page?.path,
    });
  }

  function homeSectionName(section) {
    if (!section) return 'unknown';

    const knownSections = [
      ['or-hero', 'hero'],
      ['or-route-stories', 'route_stories'],
      ['or-category', 'category_grid'],
      ['or-featured', 'featured_products'],
      ['or-tested-builds', 'tested_builds'],
      ['or-used-marketplace', 'used_marketplace'],
      ['or-story', 'story_band'],
      ['or-brands', 'brand_grid'],
      ['or-journal', 'journal_grid'],
      ['or-cta', 'cta'],
      ['or-sidebar-ad', 'expo_sidebar_ad'],
      ['or-pdp-ad', 'expo_pdp_ad'],
    ];

    const match = knownSections.find(([className]) => section.classList.contains(className));
    return match ? match[1] : 'home_section';
  }

  function interactionType(link) {
    if (link.matches('[data-or-hero-cta]')) return 'hero_cta';
    if (link.matches('[data-or-sidebar-ad-cta], [data-or-promo-ad-cta]')) return 'expo_banner_click';
    if (link.closest('.or-product-card, [data-or-collection-feature-product]')) return 'product_click';
    if (link.closest('.or-category-card')) return 'category_click';
    if (link.closest('.or-brand-card')) return 'brand_click';
    if (link.closest('.or-journal-card')) return 'article_click';
    if (link.closest('.or-route-story')) return 'route_story_click';
    if (link.closest('.or-btn')) return 'cta_click';
    if (link.closest('.or-section-head')) return 'section_header_click';
    return 'link_click';
  }

  function pushHomeInteraction(link) {
    const homeSection = link?.closest('.or-home');
    if (!homeSection || !link) return;

    push({
      event: 'or_home_interaction',
      interaction_type: interactionType(link),
      section_name: homeSectionName(homeSection),
      link_url: link.href,
      link_text: (link.innerText || link.getAttribute('aria-label') || '').trim(),
      page_type: state.page?.type,
      page_path: state.page?.path,
    });
  }

  function buttonInteractionType(button) {
    if (button.matches('[data-or-hero-dot]')) return 'hero_slide_control';
    return 'button_click';
  }

  function pushHomeButtonInteraction(button) {
    const homeSection = button?.closest('.or-home');
    if (!homeSection || !button) return;

    push({
      event: 'or_home_interaction',
      interaction_type: buttonInteractionType(button),
      section_name: homeSectionName(homeSection),
      link_url: '',
      link_text: (button.innerText || button.getAttribute('aria-label') || '').trim(),
      page_type: state.page?.type,
      page_path: state.page?.path,
    });
  }

  function itemFromVariantId(variantId) {
    const id = String(variantId || '');
    return state.productVariants?.[id] || state.cart?.items?.find((item) => String(item.variant_id) === id);
  }

  function itemFromCartLine(line) {
    const index = Number(line || 0) - 1;
    return state.cart?.items?.[index];
  }

  function selectedItemFromLink(link) {
    const items = state.list?.items || [];
    const targetPath = normalizePath(link.href);
    return items.find((item) => normalizePath(item.url) === targetPath || targetPath.includes(normalizePath(item.url)));
  }

  function parseRequestBody(body) {
    if (!body) return {};
    if (body instanceof FormData) return Object.fromEntries(body.entries());
    if (body instanceof URLSearchParams) return Object.fromEntries(body.entries());
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (error) {
        return Object.fromEntries(new URLSearchParams(body).entries());
      }
    }
    if (typeof body === 'object') return body;
    return {};
  }

  function captureFetchEvents() {
    const originalFetch = window.fetch;
    if (!originalFetch) return;

    window.fetch = function (...args) {
      const request = args[0];
      const init = args[1] || {};
      const url = typeof request === 'string' ? request : request?.url;
      const body = init.body || request?.body;
      const bodyData = parseRequestBody(body);

      return originalFetch.apply(this, args).then((response) => {
        const path = normalizePath(url);

        if (response.ok && path.includes('/cart/add')) {
          response
            .clone()
            .json()
            .then((payload) => {
              const items = payload.items || [payload];
              items.forEach((line) => {
                const item = itemFromVariantId(line.id) || itemFromVariantId(line.variant_id);
                pushAddToCart(item || line, line.quantity || bodyData.quantity || 1);
              });
            })
            .catch(() => {
              pushAddToCart(itemFromVariantId(bodyData.id), bodyData.quantity || 1);
            });
        }

        if (response.ok && path.includes('/cart/change')) {
          const quantity = Number(bodyData.quantity);
          if (quantity === 0) {
            pushRemoveFromCart(itemFromVariantId(bodyData.id) || itemFromCartLine(bodyData.line), 1);
          }
        }

        return response;
      });
    };
  }

  function bindClickEvents() {
    document.addEventListener(
      'click',
      (event) => {
        const homeLink = event.target.closest('.or-home a[href]');
        if (homeLink) {
          pushHomeInteraction(homeLink);
        }

        const homeButton = event.target.closest('.or-home button');
        if (homeButton) {
          pushHomeButtonInteraction(homeButton);
        }

        const whatsappLink = event.target.closest('a[href*="wa.me"], a[href*="api.whatsapp.com"]');
        if (whatsappLink) {
          pushWhatsAppClick(whatsappLink);
        }

        const productLink = event.target.closest('a[href*="/products/"]');
        if (productLink) {
          const collectionFeatureLink = productLink.closest('[data-or-collection-feature-product]');
          if (collectionFeatureLink) {
            pushCollectionFeatureClick(collectionFeatureLink);
            return;
          }

          pushSelectItem(selectedItemFromLink(productLink));
        }

        const checkoutButton = event.target.closest('button[name="checkout"], a[href*="/checkout"]');
        if (checkoutButton) {
          pushBeginCheckout();
        }
      },
      true
    );
  }

  function bindSubmitFallback() {
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form?.action || !normalizePath(form.action).includes('/cart/add')) return;

        const formData = new FormData(form);
        pushAddToCart(itemFromVariantId(formData.get('id')), formData.get('quantity') || 1);
      },
      true
    );
  }

  pushInit();
  pushViewItem();
  pushViewItemList();
  pushSearch();
  pushViewCart();
  captureFetchEvents();
  bindClickEvents();
  bindSubmitFallback();
})();
